import { useState } from "react";
import { logger } from "../../lib/logger";
import {
  parseTemplateCsv,
  type TemplateCsvItem,
  type TemplateCsvParseResult,
  type TemplateCsvSkippedRowReason,
} from "../../lib/parseTemplateCsv";
import { CloseIcon } from "../Icons";

interface TemplateCsvImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** 取り込み先への保存処理。成功したら true を返す */
  onImport: (items: readonly TemplateCsvItem[]) => Promise<boolean>;
  loading: boolean;
  error: string | null;
  onImported: () => void;
}

const SKIPPED_ROW_REASON_LABELS: Record<TemplateCsvSkippedRowReason, string> = {
  columns_missing: "列数が不足しています",
  item_name_and_question_empty: "項目・質問のいずれにも内容がありません",
};

const COLUMN_DEFINITIONS = [
  { name: "カテゴリ", description: "項目の分類（例: チェックイン）" },
  { name: "時間目安", description: "例: 〜3min." },
  {
    name: "項目",
    description:
      "項目名。⭐を付けると必須項目になります。空欄でも質問があれば台本指示行として取り込まれます",
  },
  { name: "インタビュアーの質問/発話", description: "台本となる質問文" },
  { name: "質問の意図etcに関する補足", description: "補足メモ" },
];

export function TemplateCsvImportDialog({
  open,
  onOpenChange,
  title,
  onImport,
  loading,
  error,
  onImported,
}: TemplateCsvImportDialogProps) {
  const [templateCsvText, setTemplateCsvText] = useState("");
  const [parseResult, setParseResult] = useState<TemplateCsvParseResult | null>(
    null,
  );
  const [showImportError, setShowImportError] = useState(false);

  if (!open) return null;

  const closeDialog = () => {
    // 入稿中に閉じると完了を拾えず、置き換わった項目が画面に反映されないまま終わる
    if (loading) return;
    setParseResult(null);
    setShowImportError(false);
    onOpenChange(false);
  };

  const backToInput = () => {
    setParseResult(null);
    setShowImportError(false);
  };

  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!templateCsvText.trim()) {
      alert("CSVテキストを入力してください");
      return;
    }

    setShowImportError(false);
    setParseResult(parseTemplateCsv(templateCsvText));
  };

  const handleTemplateImport = async () => {
    if (!parseResult) return;

    // onImport は内部で例外を処理して false を返し、詳細を error に入れる
    const imported = await onImport(parseResult.items);
    if (imported) {
      closeDialog();
      setTemplateCsvText("");
      onImported();
      alert("インタビューテンプレートを登録しました");
    } else {
      logger.error(new Error("template csv import failed"), {
        feature: "TemplateCsvImportDialog",
        itemCount: parseResult.items.length,
      });
      setShowImportError(true);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={closeDialog}
    >
      <div
        className="max-h-[90vh] w-[90%] max-w-[900px] overflow-auto rounded-xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 flex items-center justify-between">
          <h3 className="font-bold text-lg">{title}</h3>
          <button
            type="button"
            onClick={closeDialog}
            disabled={loading}
            className="cursor-pointer border-none bg-none p-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        {parseResult === null ? (
          <form onSubmit={handlePreview} className="flex flex-col gap-4">
            <div className="rounded-md bg-[var(--color-bg-subtle-gray)] p-4 text-sm">
              <p className="mb-2 font-medium">貼り付け方法</p>
              <ul className="mb-3 list-disc pl-5 text-[var(--color-text-secondary)]">
                <li>
                  スプレッドシートで範囲を選択してコピーし、そのまま貼り付けられます（タブ区切りに対応）
                </li>
                <li>
                  「ファイル &gt; ダウンロード &gt;
                  カンマ区切り形式（.csv）」でダウンロードしたファイルの中身を貼り付けることもできます
                </li>
                <li>6列目以降の列があっても無視されます</li>
              </ul>
              <p className="mb-2 font-medium">
                列の並び（1行目はヘッダーとして無視されます）
              </p>
              <table className="mb-3 w-full border-collapse text-left">
                <thead>
                  <tr>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      列
                    </th>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      内容
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COLUMN_DEFINITIONS.map((column, index) => (
                    <tr key={column.name}>
                      <td className="border border-[var(--color-border-default)] px-2 py-1">
                        {index + 1}. {column.name}
                      </td>
                      <td className="border border-[var(--color-border-default)] px-2 py-1 text-[var(--color-text-secondary)]">
                        {column.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[var(--color-text-secondary)]">
                インポートすると既存のテンプレート項目はすべて削除され、貼り付けた内容に置き換わります。
              </p>
            </div>
            <div>
              <label className="mb-1 block font-medium text-sm">
                CSVテキストを貼り付け
              </label>
              <textarea
                value={templateCsvText}
                onChange={(e) => setTemplateCsvText(e.target.value)}
                rows={15}
                placeholder="カテゴリ,時間目安,項目,インタビュアーの質問/発話,質問の意図etcに関する補足&#10;チェックイン,〜3min.,⭐あいさつ,本日はお忙しい中...,"
                className="w-full resize-y rounded border border-[var(--color-border-default)] p-2 font-mono text-sm"
              />
            </div>
            <button
              type="submit"
              className="cursor-pointer rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2.5 font-medium text-sm text-white"
            >
              プレビューを確認
            </button>
          </form>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-sm">
              {parseResult.items.length > 0
                ? `以下の ${parseResult.items.length} 件を取り込みます。既存のテンプレート項目はすべて削除され、この内容に置き換わります。`
                : "取り込める行がありません。1行目をヘッダーとして、項目または質問のいずれかに内容がある3列以上の行を貼り付けてください。"}
            </p>
            {parseResult.hasUnterminatedQuote && (
              <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-danger)] p-3 text-sm">
                <p className="font-medium text-[var(--color-text-danger)]">
                  閉じられていないクォート（"）があります。以降の内容が1つのセルにまとめて取り込まれている可能性があるため、プレビューの内容を確認してください。
                </p>
              </div>
            )}
            {parseResult.skippedRows.length > 0 && (
              <div className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-danger)] p-3 text-sm">
                <p className="mb-1 font-medium text-[var(--color-text-danger)]">
                  {parseResult.skippedRows.length}{" "}
                  行を解釈できませんでした。以下の行は取り込まれません。
                </p>
                <ul className="list-disc pl-5 text-[var(--color-text-secondary)]">
                  {parseResult.skippedRows.map((row) => (
                    <li key={row.lineNumber} className="break-all">
                      {row.lineNumber}行目（
                      {SKIPPED_ROW_REASON_LABELS[row.reason]}
                      ）: {row.content}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="max-h-[40vh] overflow-auto">
              <table className="w-full border-collapse text-left text-sm">
                <thead>
                  <tr>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      必須
                    </th>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      カテゴリ
                    </th>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      時間目安
                    </th>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      項目
                    </th>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      質問/発話
                    </th>
                    <th className="border border-[var(--color-border-default)] px-2 py-1">
                      補足
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parseResult.items.map((item) => (
                    <tr key={item.order} className="align-top">
                      <td className="border border-[var(--color-border-default)] px-2 py-1 text-center">
                        {item.isRequired ? "必須" : ""}
                      </td>
                      <td className="border border-[var(--color-border-default)] px-2 py-1">
                        {item.category}
                      </td>
                      <td className="border border-[var(--color-border-default)] px-2 py-1">
                        {item.timeEstimate}
                      </td>
                      <td className="border border-[var(--color-border-default)] px-2 py-1">
                        {item.itemName}
                      </td>
                      <td className="whitespace-pre-wrap border border-[var(--color-border-default)] px-2 py-1">
                        {item.question}
                      </td>
                      <td className="whitespace-pre-wrap border border-[var(--color-border-default)] px-2 py-1">
                        {item.note}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {showImportError && (
              <p className="text-[var(--color-text-danger)] text-sm">
                {error ?? "テンプレートの登録に失敗しました"}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={backToInput}
                disabled={loading}
                className="cursor-pointer rounded-md border border-[var(--color-border-default)] bg-white px-4 py-2.5 font-medium text-sm disabled:cursor-not-allowed disabled:opacity-50"
              >
                入力に戻る
              </button>
              <button
                type="button"
                onClick={handleTemplateImport}
                disabled={loading || parseResult.items.length === 0}
                className="cursor-pointer rounded-md border-none bg-[var(--color-primary-500)] px-4 py-2.5 font-medium text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "インポート中..." : "インポートする"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
